import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { cache } from '@common/util/cache.operator';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ConfirmDialogComponent } from '@portal-core/general/components/confirm-dialog/confirm-dialog.component';
import { LiveLogSourceLinkResolver } from '@portal-core/live-log/components/live-log/live-log.component';
import { LogTabPanelComponent } from '@portal-core/live-log/components/log-tab-panel/log-tab-panel.component';
import { LogPrepareStatus } from '@portal-core/live-log/enums/log-prepare-status.enum';
import { LogStreamService } from '@portal-core/live-log/services/log-stream.service';
import { CentralPermissions } from '@portal-core/permissions/enums/central-permissions.enum';
import { PermissionsService } from '@portal-core/permissions/services/permissions.service';
import { ProcessAttemptStatus } from '@portal-core/processes/enums/process-attempt-status.enum';
import { ProfilesService } from '@portal-core/profiles/services/profiles.service';
import { ProjectStatus } from '@portal-core/projects/enums/project-status.enum';
import { Project } from '@portal-core/projects/models/project.model';
import { ProjectsService } from '@portal-core/projects/services/projects.service';
import { ProcessPublicationType } from '@portal-core/publications/enums/process-publication-type.enum';
import { PublicationProfileTab } from '@portal-core/publications/enums/publication-profile-tab.enum';
import { Publication } from '@portal-core/publications/models/publication.model';
import { PublicationsApiService } from '@portal-core/publications/services/publications-api.service';
import { PublicationsService } from '@portal-core/publications/services/publications.service';
import { RoutingService } from '@portal-core/routing/services/routing.service';
import { TargetsService } from '@portal-core/targets/services/targets.service';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { LoadingState } from '@portal-core/util/loading-state';
import { pollUntil } from '@portal-core/util/polling';
import { BehaviorSubject, catchError, combineLatest, filter, first, map, noop, Observable, of, Subscription, switchMap } from 'rxjs';

export interface PublicationProfileDialogData {
  publicationId: number;
  publicationProfileTab?: PublicationProfileTab;
}

enum PublicationProfileForm {
  Delete,
  Settings
}

@Component({
  selector: 'mc-publication-profile-dialog',
  templateUrl: './publication-profile-dialog.component.html',
  styleUrls: ['./publication-profile-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PublicationProfileDialogComponent extends DialogBase implements OnDestroy, OnInit {
  /** Defines the default config for the dialog. */
  static DialogConfig: MatDialogConfig = {
    width: '120rem',
    height: '68rem',
    // Keeps focus off the floating maximize/close buttons on open (no mcDialogAutoFocus target here).
    autoFocus: 'dialog',
  };

  PublicationProfileForm: typeof PublicationProfileForm = PublicationProfileForm;
  PublicationProfileTab: typeof PublicationProfileTab = PublicationProfileTab;
  ProcessAttemptStatus: typeof ProcessAttemptStatus = ProcessAttemptStatus;

  publication$: Observable<Publication>;
  publicationId: number;
  publicationProfileTab: PublicationProfileTab;

  // Set after the prepare endpoint reports `Ready`. Bound into `<mc-live-log [streamUrl]>`.
  // The live-log / log-stream-grid children only open EventSource once this becomes truthy
  // (their `ngOnChanges` ignores undefined). The shared LogStreamService caches the multicast
  // keyed by URL, so the anchor subscription and the children end up sharing one EventSource.
  logStreamUrl: string;
  // Tracks the prepare-endpoint polling: loading$ during the polling window, generalError$ /
  // detailedErrors$ if a request fails or polling times out. The child surfaces (live-log,
  // log-stream-grid) read these to render the loader / error panel.
  logPrepareLoadingState: LoadingState<string> = new LoadingState<string>();
  // True when the prepare endpoint reported `Unavailable` — no log artifacts on disk and Redis
  // is empty. Children render "No log is available for this publication." in their empty state.
  // A BehaviorSubject (rather than a plain field) so canDownloadLog$ can react to it once the
  // prepare-endpoint poll resolves, since publication.CanDownloadLog alone doesn't know about it.
  logUnavailable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  get emptyLabelOverride(): string | null {
    if (this.logUnavailable$.value) return 'No log is available for this publication.';
    return null;
  }

  // Captured from the latest publication$/project$ emissions so `resolvePublicationSourceLink`
  // (a stable arrow function bound into `<mc-live-log [sourceLinkResolver]>`) has the context
  // it needs without re-subscribing each line.
  private currentPublication: Publication | null = null;
  private currentProject: Project | null = null;

  // Build the workspace router link for a source-file annotation. Publication source files
  // belong to the associated build's project / branch, so the route is computed off the
  // captured publication.Build + project. Defined as an arrow property so the input binding
  // stays referentially stable.
  resolvePublicationSourceLink: LiveLogSourceLinkResolver = (src: string) => {
    const build = this.currentPublication?.Build;
    if (!src || !build || !this.currentProject || this.currentProject.LicenseId === undefined) {
      return null;
    }
    const path = this.routingService.getProjectFilePath(this.currentProject.LicenseId, this.currentProject.Id, build.BranchName, src);
    return typeof path === 'undefined'
      ? ['/', this.currentProject.LicenseId, 'projects', this.currentProject.Id, 'workspace', 'file', encodeURIComponent(build.BranchName), encodeURIComponent(src)]
      : path;
  };

  // The Log tab's shared UI (Download/Find/Settings buttons + Log/Grid sub-tabs) — used by the
  // maximize button handler to re-measure the live-log's virtual scroll viewport after a
  // programmatic dialog resize.
  @ViewChild(LogTabPanelComponent) logTabPanel: LogTabPanelComponent;

  licenseId$: Observable<number>;
  loadingState: LoadingState<string> = new LoadingState<string>();
  project$: Observable<Project>;
  projectIsLocked$: Observable<boolean>;
  reload$: BehaviorSubject<void> = new BehaviorSubject(undefined);
  routerEventSubscription: Subscription;
  logStreamAnchorSubscription: Subscription;
  logPrepareSubscription: Subscription;
  currentPublicationSubscription: Subscription;
  currentProjectSubscription: Subscription;
  showCancelPublicationTab$: Observable<boolean>;
  showDeleteTab$: Observable<boolean>;
  showSettingsTab$: Observable<boolean>;
  showLogTab$: Observable<boolean>;
  canDownloadLog$: Observable<boolean>;
  userCanManagePublications$: Observable<boolean>;

  constructor(
    protected dialog: MatDialog,
    protected dialogRef: MatDialogRef<PublicationProfileDialogComponent>,
    private router: Router,
    private snackBar: MatSnackBar,
    private errorService: ErrorService,
    private publicationsService: PublicationsService,
    private targetsService: TargetsService,
    private projectsService: ProjectsService,
    private permissionsService: PermissionsService,
    private profilesService: ProfilesService,
    private changeDetectorRef: ChangeDetectorRef,
    private logStreamService: LogStreamService,
    private publicationsApiService: PublicationsApiService,
    private routingService: RoutingService,
    @Inject(MAT_DIALOG_DATA) private data: PublicationProfileDialogData
  ) {
    super(dialog, dialogRef);
  }

  ngOnInit() {
    super.ngOnInit();

    this.publicationId = this.data.publicationId;

    // Create an observable of the publication. Start with reload$ so that the data can be reloaded as needed
    this.publication$ = this.reload$.pipe(
      switchMap(() => this.profilesService.getPublicationProfile$(this.publicationId)),
      // Switch to the publication in the data store. The publication is returned in the profile data so there is no need to make a request to the API
      switchMap(publication => this.publicationsService.getItemById$(publication.Id, { allowApiRequest: false })),
      cache()
    );

    // Create an observable for the publication's project id
    const projectId$ = this.publication$.pipe(
      map(publication => publication.ProjectId)
    );

    this.project$ = projectId$.pipe(
      // The project is returned in the profile data so there is no need to make a request to the API
      switchMap(projectId => this.projectsService.getItemById$(projectId, { allowApiRequest: false })),
      cache()
    );

    // Create an observable for the license id. The publication doesn't have a license id so grab it from the project.
    this.licenseId$ = this.project$.pipe(
      map(project => project?.LicenseId)
    );

    // Create an observable for locked status of the publication's project
    this.projectIsLocked$ = this.project$.pipe(
      map(project => project?.Status === ProjectStatus.Locked)
    );

    // Create an observable for the user's access to the publication's project
    const userHasProjectAccess$ = this.project$.pipe(
      map(project => project?.UserHasAccess)
    );

    // Create observables for the user's permissions
    this.userCanManagePublications$ = projectId$.pipe(
      switchMap(projectId => this.permissionsService.currentUserHasPermission$(CentralPermissions.PublishBuilds, projectId))
    );

    // Create observables for which tabs to show in the sidebar
    this.showDeleteTab$ = combineLatest([this.userCanManagePublications$, userHasProjectAccess$, this.publication$, this.projectIsLocked$]).pipe(
      map(([userCanManagePublications, userHasProjectAccess, publication, projectIsLocked]) => userCanManagePublications && userHasProjectAccess && publication.IsFinished && !projectIsLocked)
    );
    this.showSettingsTab$ = combineLatest([this.userCanManagePublications$, userHasProjectAccess$, this.projectIsLocked$]).pipe(
      map(([userCanManagePublications, userHasProjectAccess, projectIsLocked]) => userCanManagePublications && userHasProjectAccess && !projectIsLocked)
    );
    this.showCancelPublicationTab$ = combineLatest([userHasProjectAccess$, this.publication$, this.projectIsLocked$]).pipe(
      map(([userHasProjectAccess, publication, projectIsLocked]) => userHasProjectAccess && publication.CanBeCanceled && !projectIsLocked)
    );

    const userCanReadLog$: Observable<boolean> = this.publicationsService.getAccess$({ Resource: { Id: this.publicationId }, Operations: ['LogRead'] }).pipe(
      map(access => !!access.LogRead),
      // Hide the tab rather than failing the whole dialog if the access check itself errors.
      catchError(() => of(false)),
      cache()
    );
    this.canDownloadLog$ = combineLatest([userCanReadLog$, this.publication$, this.logUnavailable$]).pipe(
      map(([userCanReadLog, publication, logUnavailable]) => userCanReadLog && publication.CanDownloadLog && !logUnavailable)
    );
    this.showLogTab$ = userCanReadLog$;

    this.loadingState.update(true);

    // Start the initial load
    combineLatest([
      this.publication$,
      this.projectIsLocked$,
      userHasProjectAccess$,
      this.userCanManagePublications$,
      userCanReadLog$
    ]).pipe(
      first()
    ).subscribe(() => {
      // Set the initial tab
      if (typeof this.data.publicationProfileTab === 'number') {
        this.publicationProfileTab = this.data.publicationProfileTab;
      } else {
        this.publicationProfileTab = PublicationProfileTab.Overview;
      }

      this.loadingState.update(false);
    }, error => {
      this.loadingState.update(false, 'Unable to load the publication.', this.errorService.getErrorMessages(error));
    });

    // Resolve the log via the prepare endpoint before opening the SSE stream. Tier 1: the
    // publication is running or just finished, Redis is hot, Ready on first call. Tier 2:
    // Redis was evicted but a `.mcjlog` snapshot exists on blob — the resolver loads it into
    // Redis and returns Ready. Tier 3: a legacy `.mclog` exists but no `.mcjlog` yet — the
    // resolver enqueues the converter and returns Loading; we keep polling until the worker
    // writes the snapshot and the next poll lands in tier 2. No log artifacts at all →
    // Unavailable. While polling, the children show "Loading historical log…" in their empty
    // state.
    this.logPrepareLoadingState.update(true);
    this.logPrepareSubscription = pollUntil(
      () => this.publicationsApiService.preparePublicationLog$(this.publicationId),
      response => response.Status === LogPrepareStatus.Ready || response.Status === LogPrepareStatus.Unavailable,
      { delayMs: 2000, timeoutMs: 120_000, emitWhenDoneOnly: true }
    ).subscribe({
      next: response => {
        this.logPrepareLoadingState.update(false);
        if (response.Status === LogPrepareStatus.Ready) {
          this.logStreamUrl = this.publicationsApiService.getPublicationLogStreamUrl(this.publicationId);
          this.logStreamAnchorSubscription = this.logStreamService.getStream$(this.logStreamUrl).subscribe();
        } else {
          this.logUnavailable$.next(true);
        }
        // OnPush parent: setting the field doesn't itself trigger CD, so the child's
        // [streamUrl] / [loading] inputs wouldn't repaint until the next external trigger
        // (e.g. tab switch). markForCheck flushes the bindings now.
        this.changeDetectorRef.markForCheck();
      },
      error: error => {
        const isTimeout = error?.name === 'TimeoutError';
        this.logPrepareLoadingState.update(
          false,
          isTimeout ? 'Log preparation is taking longer than expected. Try again shortly.' : 'Unable to load the log.',
          isTimeout ? undefined : this.errorService.getErrorMessages(error)
        );
        this.changeDetectorRef.markForCheck();
      }
    });

    // Capture the latest publication / project for the source-link resolver. cache() above
    // ensures these are shareReplay'd, so subscribing here doesn't trigger duplicate API
    // requests.
    this.currentPublicationSubscription = this.publication$.subscribe(publication => this.currentPublication = publication);
    this.currentProjectSubscription = this.project$.subscribe(project => this.currentProject = project);

    // Listen for successful navigations and close the dialog (for closing the dialog when the user clicks a link in the dialog)
    this.routerEventSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => this.dialogRef.close());
  }

  ngOnDestroy() {
    this.logStreamAnchorSubscription?.unsubscribe();
    // Completed streams are only ever released explicitly (see LogStreamService) — this dialog
    // closing is the boundary that says the shared stream is no longer needed by anyone.
    if (this.logStreamUrl) {
      this.logStreamService.release(this.logStreamUrl);
    }
    this.logPrepareSubscription?.unsubscribe();
    this.routerEventSubscription?.unsubscribe();
    this.currentPublicationSubscription?.unsubscribe();
    this.currentProjectSubscription?.unsubscribe();
  }

  onCancelPublicationClicked(publication: Publication) {
    this.dialog.open(ConfirmDialogComponent, {
      width: '36rem',
      data: {
        prompt: 'Are you sure you want to cancel this publication?',
        action: 'OK'
      }
    }).afterClosed().pipe(
      filter(confirmed => confirmed),
      switchMap(() => this.publicationsService.processPublication$(publication.Id, ProcessPublicationType.Cancel))
    ).subscribe(noop, () => {
      this.snackBar.open('Unable to cancel the publication.', 'OK', { duration: SnackBarAutoDismissDurationMS });
    });
  }

  onDownloadLogClicked(publication: Publication) {
    const logUrl = publication.Build.PrivateOutputUrl.substring(0, publication.Build.PrivateOutputUrl.lastIndexOf('/') + 1) + 'publication/' + publication.Id + '/' + this.getTargetFileName(publication.Build.TargetPath) + '.mclog';
    window.open(logUrl, '_blank');
  }

  getTargetFileName(targetPath: string): string {
    return this.targetsService.getTargetFileNameFromPath(targetPath);
  }

  onCancel() {
    this.closeDialog();
  }

  onSaved(publicationProfileForm: PublicationProfileForm) {
    switch (publicationProfileForm) {
      case PublicationProfileForm.Delete:
        this.closeDialog();
        break;
      // If the settings were updated then refresh the publication data to get the latest data
      case PublicationProfileForm.Settings:
        this.reload();
        break;
    }
  }

  getColorForPublicationState(publication: Publication): string {
    return this.publicationsService.getColorForPublication(publication, 'muted');
  }

  getTextColorClassForPublicationState(publication: Publication): string {
    return `mc-text-${this.getColorForPublicationState(publication)}`;
  }

  getStatusDisplayText(publication: Publication): string {
    return this.publicationsService.getStatusDisplayText(publication);
  }

  reload() {
    this.reload$.next();
  }
}
